<template>
  <section class="screen">
    <!-- SUBMITTING -->
    <template v-if="phase === 'submitting'">
      <div class="s-body stack gap-18 center" style="justify-content: center">
        <div class="spinner"></div>
        <div class="stack gap-6 center">
          <h2>در حال ارسال اطلاعات</h2>
          <p>لطفاً چند لحظه صبر کنید…</p>
        </div>
      </div>
    </template>

    <!-- REFUSED — the server answered, and said no -->
    <template v-else-if="phase === 'failed'">
      <div class="s-body stack gap-18 center" style="justify-content: center">
        <div class="iconcircle warn"><base-icon name="alert" /></div>

        <div class="stack gap-10 center">
          <h2>ثبت اطلاعات انجام نشد</h2>
          <p>{{ GENERIC_ERROR }}</p>
          <p v-if="submitDetail" class="detail">{{ submitDetail }}</p>
        </div>

        <div class="note" style="text-align: right">
          <base-icon name="check" />
          <span>اطلاعات واردشده ذخیره شده است؛ با تلاش مجدد از همین مرحله ادامه می‌دهید.</span>
        </div>

        <div class="grow"></div>
      </div>

      <div class="s-foot">
        <button class="btn btn-primary" type="button" @click="retry">
          <base-icon name="refresh" />
          <span>تلاش مجدد</span>
        </button>
        <button class="btn btn-outline" type="button" @click="backToReview">
          بازگشت به بازبینی
        </button>
        <support-link @reveal="supportOpen = true">
          در صورت تکرار خطا، با پشتیبانی تماس بگیرید
        </support-link>
      </div>
    </template>

    <!-- DONE -->
    <template v-else>
      <div class="s-body stack gap-18 center" style="justify-content: center">
        <brand-emblem small />

        <div class="iconcircle ok"><base-icon name="check-circle" /></div>

        <div class="stack gap-10 center">
          <h2>اطلاعات شما با موفقیت ثبت شد</h2>
          <p>احراز هویت شما در حال بررسی است.</p>
        </div>

        <div class="stack gap-6 center">
          <div class="codelbl">کد رهگیری</div>
          <div class="coderow">
            <div class="code">{{ faTrackingId }}</div>
            <copy-button :value="trackingId" label="کپی کد رهگیری" />
          </div>
          <p class="codehint">
            این کد را نزد خود نگه دارید؛ برای پیگیری‌های بعدی به آن نیاز خواهید داشت.
          </p>
        </div>

        <div class="note" style="text-align: right">
          <base-icon name="message" />
          <span>
            نتیجه‌ی احراز هویت حدود ۱۵ دقیقه‌ی دیگر، از طریق پیامک و توسط سامانه‌ی ثنا به شما
            اطلاع داده می‌شود.
          </span>
        </div>

        <div class="grow"></div>
      </div>

      <div class="s-foot">
        <button v-if="returnUrl" class="btn btn-primary" type="button" @click="handBack">
          بازگشت به سامانه‌ی ثنا
        </button>
        <support-link @reveal="supportOpen = true">
          در صورت مشکل، با پشتیبانی تماس بگیرید
        </support-link>
      </div>
    </template>

    <support-sheet :open="supportOpen" @close="supportOpen = false" />

    <network-error-dialog
      :kind="netError"
      :busy="checking"
      @retry="retry"
      @back="backToReview"
    />
  </section>
</template>

<script>
import BrandEmblem from "@/components/BrandEmblem.vue";
import BaseIcon from "@/components/BaseIcon.vue";
import CopyButton from "@/components/CopyButton.vue";
import SupportLink from "@/components/SupportLink.vue";
import SupportSheet from "@/components/SupportSheet.vue";
import NetworkErrorDialog from "@/components/NetworkErrorDialog.vue";
import { RETURN_TOKEN_PREFIX } from "@/constants";
import { isOffline } from "@/services/connectivity";
import { session } from "@/services/session";
import { playSuccessChime } from "@/utils/chime";
import { toFa } from "@/utils/digits";

const GENERIC_ERROR = "ارسال اطلاعات به سامانه انجام نشد. لطفاً دوباره تلاش کنید.";

/**
 * What the backend said, as a support-facing detail line.
 *
 * Shown under the generic message rather than instead of it: the API's own text is not
 * always Persian and not always written for an end user, but it is the only thing that
 * makes a support call actionable, so it is kept — quietly.
 */
function serverDetail(response) {
  if (!response) return "";
  const data = response.data;
  const message = data && typeof data.message === "string" ? data.message.trim() : "";
  const status = response.status ? `کد ${response.status}` : "";
  return [message, status].filter(Boolean).join(" · ");
}

/**
 * Submit, then hand back.
 *
 * The submit itself is the reachability test. Only a device that reports itself offline
 * is stopped beforehand, because uploading several megabytes into a dead radio is worth
 * avoiding; everything else is decided by what the request actually does.
 *
 * A failure is then split by whether the backend answered. A response —
 * any response, including a 4xx or 5xx — proves the host was reachable, so it is a
 * refusal, not a connectivity problem; conflating the two told users to switch off a VPN
 * that was never involved and hid the only useful detail, what the server actually said.
 *
 * Nothing is cleared on failure. The artefacts stay in the store and IndexedDB, so
 * "retry" genuinely resumes rather than restarting, and "back to review" is safe.
 */
export default {
  name: "CompleteView",
  components: { BrandEmblem, BaseIcon, CopyButton, SupportLink, SupportSheet, NetworkErrorDialog },

  data() {
    return {
      /** "submitting" | "failed" | "done" */
      phase: "submitting",
      /** The backend's own words, when it gave any. */
      submitDetail: "",
      supportOpen: false,
      GENERIC_ERROR,
    };
  },

  computed: {
    trackingId() {
      return this.$store.state.flow.trackingId || this.$store.state.session.trackingId;
    },
    faTrackingId() {
      return toFa(this.trackingId || "—");
    },
    returnUrl() {
      return this.$store.state.session.returnUrl;
    },
    netError() {
      return this.$store.state.ui.netError;
    },
    checking() {
      return this.$store.state.ui.checkingConnection;
    },
  },

  created() {
    this.attemptSubmit();
  },

  beforeDestroy() {
    // Leaving with the dialog up would strand it over the review screen.
    this.$store.commit("ui/SET_NET_ERROR", null);
  },

  methods: {
    async attemptSubmit() {
      this.phase = "submitting";
      this.submitDetail = "";
      this.$store.commit("ui/SET_NET_ERROR", null);

      // Airplane mode is worth catching before uploading several megabytes. Anything
      // subtler is not: the old pre-flight probe fetched the API root, and a root that
      // does not answer a bare GET — or answers slower than the 6.5s timeout on a mobile
      // connection — blocked a submit that would have gone through, and told the user it
      // was their VPN. Let the real request be the test of whether the API is reachable.
      if (isOffline()) {
        this.$store.commit("ui/SET_NET_ERROR", "offline");
        return;
      }

      try {
        await this.$store.dispatch("flow/submit");
        await this.onSubmitted();
      } catch (error) {
        await this.onSubmitFailed(error);
      }
    },

    /**
     * Two kinds of failure, two different screens.
     *
     * `error.response` exists only when the backend replied, which settles reachability:
     * whatever went wrong, it was not a blocked route to the API. That case gets a plain
     * error screen with the server's own words.
     *
     * No response means the request never completed — a dropped connection, a DNS or TLS
     * failure, a blocked host. Only there is the connectivity dialog the right answer, and
     * a fresh probe decides which of its two messages to show.
     */
    async onSubmitFailed(error) {
      if (error && error.response) {
        this.$store.commit("ui/SET_NET_ERROR", null);
        this.submitDetail = serverDetail(error.response);
        this.phase = "failed";
        return;
      }

      const kind = await this.$store.dispatch("ui/revalidate");
      // The probe passing while the POST did not is unusual but real (a large body dropped
      // mid-flight, for one). "server" is the closest honest label.
      this.$store.commit("ui/SET_NET_ERROR", kind === "ok" ? "server" : kind);
    },

    /**
     * The flow is finished: the session credential is spent and the saved progress is no
     * longer something to resume. The tracking id and return URL survive, because the
     * done screen still shows one and hands back with the other.
     */
    async onSubmitted() {
      await this.$store.dispatch("flow/finish");
      this.$store.dispatch("session/finish");
      this.phase = "done";
      // Sounded on arrival at the done screen, not on the successful response: the chime
      // confirms what the user is looking at. A retry that finally lands still gets one,
      // since that is the first time this screen has been reached.
      playSuccessChime();
    },

    retry() {
      this.attemptSubmit();
    },

    backToReview() {
      this.$store.commit("ui/SET_NET_ERROR", null);
      this.$router.push({ name: "check-assets" });
    },

    /**
     * Hand control back to the referring system. The `4:` prefix on the original inbound
     * token is the قوه‌قضائیه convention for "this flow completed".
     */
    handBack() {
      const returnUrl = this.returnUrl;
      const token = session.inboundToken();
      if (!returnUrl) return;
      const target = `${returnUrl}?token=${RETURN_TOKEN_PREFIX}${token}`;
      session.clear();
      window.location.replace(target);
    },
  },
};
</script>

<style scoped>
/* The server's own wording, kept legible but clearly secondary — it is there for a
   support call, not for the user to decode. */
.detail {
  font-size: 12.5px;
  line-height: 1.7;
  color: var(--muted-foreground);
  background: var(--muted);
  border-radius: 10px;
  padding: 8px 12px;
  max-width: 280px;
  word-break: break-word;
}

.codelbl {
  font-size: 12.5px;
  color: var(--muted-foreground);
  font-weight: 500;
}

.coderow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.code {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--foreground);
  font-variant-numeric: tabular-nums;
  direction: ltr;
  background: var(--muted);
  border-radius: 12px;
  padding: 10px 18px;
}

.codehint {
  font-size: 12.5px;
  line-height: 1.75;
  color: var(--muted-foreground);
  text-align: center;
  max-width: 270px;
  margin-top: 2px;
}
</style>
